/**
 * CBD HUT — facets.js (mobile-safe + Refresh/Dawn compatible)
 *
 * Fixes:
 * - Mobile facets: DO NOT auto-submit on checkbox changes, only on Apply
 * - Mobile sort: submit immediately
 * - Apply: submit then close drawer safely (without fighting menu-drawer)
 * - Prevents “open then instantly close” caused by re-calling drawer.bindEvents() (duplicate listeners)
 * - Keeps Dawn/Refresh section fetch + cache behaviour
 * - Scroll-lock failsafe (real unlock incl. iOS fixed body lock)
 */
const cbhDebounce =
  typeof debounce === 'function'
    ? debounce
    : (fn, wait = 300) => {
        let t;
        return (...args) => {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(null, args), wait);
        };
      };

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = cbhDebounce((event) => {
  this.onSubmitHandler(event);
}, 600);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      // Desktop: submit on input (instant filtering)
      // Mobile drawer: DO NOT submit on checkbox input; submit on Apply instead
      facetForm.addEventListener('input', (event) => {
        const form = event.target ? event.target.closest('form') : null;
        const isMobileDrawer = Boolean(form && form.classList.contains('mobile-facets'));
        if (isMobileDrawer) return;
        this.debouncedOnSubmit(event);
      });

      // Mobile sort dropdown should submit immediately
      facetForm.addEventListener('change', (event) => {
        const target = event.target;
        if (!target) return;

        const form = target.closest('form');
        const isMobileDrawer = Boolean(form && form.classList.contains('mobile-facets'));

        if (isMobileDrawer && target.name === 'sort_by') {
          this.onSubmitHandler(event);
        }
      });

      // Mobile Apply button: submit + close drawer
      facetForm.addEventListener('click', (event) => {
        const btn = event.target.closest('button[data-cbh-close-facets]');
        if (!btn) return;

        const form = btn.closest('form');
        const isMobileDrawer = Boolean(form && form.classList.contains('mobile-facets'));
        if (!isMobileDrawer) return;

        // Don’t let other “click-away” handlers fight this
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

        const searchParams = this.createSearchParams(form);
        this.onSubmitForm(searchParams, event);

        /**
         * CRITICAL:
         * Do NOT manually remove [open] on the outer details unless we must.
         * Close via the menu-drawer API so theme scroll unlock happens properly.
         */
        const outerDetails = btn.closest('details.mobile-facets__disclosure');
        const drawer = (outerDetails && outerDetails.closest('menu-drawer')) || btn.closest('menu-drawer');

        if (drawer && typeof drawer.closeMenuDrawer === 'function') {
          const summary = drawer.querySelector('summary');
          drawer.closeMenuDrawer(null, summary || false);
        } else if (outerDetails) {
          // Fallback close
          outerDetails.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));
          outerDetails.removeAttribute('open');
          outerDetails.classList.remove('menu-opening');
          FacetFiltersForm.unlockScroll(true);
        }

        // Final safety: ensure no lock remains once the drawer is closed
        setTimeout(() => FacetFiltersForm.unlockScroll(false), 0);
        setTimeout(() => FacetFiltersForm.unlockScroll(false), 300);
      });
    }

    // Support both Dawn default IDs and section-scoped IDs
    const facetWrapper =
      this.querySelector('#FacetsWrapperDesktop') ||
      this.querySelector('[id^="FacetsWrapperDesktop-"]') ||
      this.querySelector('[id^="FacetsWrapperDesktop"]');

    if (facetWrapper && typeof onKeyUpEscape === 'function') {
  facetWrapper.addEventListener('keyup', onKeyUpEscape);
}

    // Bind X/back/summaries immediately on first load (not only after re-render)
    FacetFiltersForm.bindMobileFacetsDrawerControls();

    // Ensure badge state is correct on first load
    FacetFiltersForm.updateMobileFilterBadge();
  }

  // -------------------------
  // Static helpers
  // -------------------------
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
      element.setAttribute('aria-disabled', disable ? 'true' : 'false');
    });
  }

  /**
   * Real unlock (classes + iOS fixed lock)
   * - Removes theme overflow hidden classes
   * - Releases CBH_SCROLL_LOCK (ref-count) safely
   * - Clears body fixed styles as a final fallback
   */
  static unlockScroll(force = false) {
    const body = document.body;
    const html = document.documentElement;

    if (html) html.classList.remove('overflow-hidden', 'no-scroll', 'scroll-lock');

    if (body) {
      body.classList.remove('overflow-hidden', 'no-scroll', 'scroll-lock', 'overflow-hidden-mobile');

      // Also remove any Shopify breakpoint overflow-hidden-* classes
      body.className = body.className
        .replace(/\boverflow-hidden-[^\s]+\b/g, '')
        .replace(/\boverflow-hidden\b/g, '')
        .trim();
    }

    // If anything is still open and we're NOT forcing, do nothing further
    if (!force) {
      const anyModalOpen = !!document.querySelector('modal-dialog[open], .media-modal[open]');
      if (anyModalOpen) return;

      const anyFacetsOpen =
        !!document.querySelector('details.mobile-facets__disclosure[open]') ||
        !!document.querySelector('details.mobile-facets__details[open]');
      if (anyFacetsOpen) return;

      const anyMenuDrawerOpen = !!document.querySelector(
        'details.menu-drawer-container[open], details[open].menu-drawer-container'
      );
      if (anyMenuDrawerOpen) return;
    }

    // Release our iOS-safe fixed lock if it got stuck
    try {
      window.CBH_SCROLL_LOCK?.reset?.();
    } catch (e) {}

    // Hard fallback: if body is still fixed, clear it
    if (body && (body.style.position === 'fixed' || body.style.top)) {
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
    }
  }
    /**
   * Global "facets open" state (used by CSS to prevent overlays like Active filters)
   */
  static setFacetsOpenState(isOpen) {
    document.documentElement.classList.toggle('cbh-facets-open', !!isOpen);
  }

  /**
   * IMPORTANT:
   * Do NOT call drawer.bindEvents() repeatedly.
   * Instead, we “top up” bindings for newly-rendered nodes with idempotent guards.
   */
  static bindMobileFacetsDrawerControls() {
  const mobileForm =
    document.getElementById('FacetFiltersFormMobile') ||
    document.querySelector('form[id^="FacetFiltersFormMobile-"]') ||
    document.querySelector('form.mobile-facets');

  if (!mobileForm) return;

  
    // Tap on the dimmed overlay (outside the panel) should close the drawer
    // (prevents accidental taps on the header/page behind)
    if (!mobileForm.dataset.cbhOverlayBound) {
      mobileForm.dataset.cbhOverlayBound = '1';
      mobileForm.addEventListener(
        'pointerdown',
        (e) => {
          // Ignore taps inside the panel
          if (e.target && e.target.closest && e.target.closest('.mobile-facets__inner')) return;

          e.preventDefault();
          e.stopPropagation();
          FacetFiltersForm.closeMobileFacetsDrawer(mobileForm);
        },
        true
      );
    }

const outerDetails =
    mobileForm.closest('details.mobile-facets__disclosure') ||
    document.querySelector('details.mobile-facets__disclosure');

  if (!outerDetails) return;

  const drawer = outerDetails.closest('menu-drawer') || mobileForm.closest('menu-drawer');
  const main =
    mobileForm.querySelector('.mobile-facets__main') ||
    outerDetails.querySelector('.mobile-facets__main');

  const sync = () => {
    // Outer drawer panel
    FacetFiltersForm.setFacetsOpenState(!!outerDetails.open);

    outerDetails.classList.toggle('menu-opening', !!outerDetails.open);

    // Inner submenus
    const subDetails = outerDetails.querySelectorAll('details.mobile-facets__details');
    subDetails.forEach((d) => d.classList.toggle('menu-opening', !!d.open));

    // Hide the main list when any submenu is open
    const anySubOpen = !!outerDetails.querySelector('details.mobile-facets__details[open]');
    if (main) main.classList.toggle('submenu-open', anySubOpen);

    // If outer closes, ensure everything is reset
    if (!outerDetails.open) {
      subDetails.forEach((d) => {
        d.removeAttribute('open');
        d.classList.remove('menu-opening');
      });
      if (main) main.classList.remove('submenu-open');
    }
  };

  // Initial state
  sync();

  // One toggle listener (idempotent)
  if (outerDetails.dataset.cbhFacetToggleBound !== '1') {
    outerDetails.dataset.cbhFacetToggleBound = '1';

    outerDetails.addEventListener(
      'toggle',
      (e) => {
        const t = e.target;
        if (!t || !t.matches) return;

        if (!t.matches('details.mobile-facets__disclosure, details.mobile-facets__details')) return;

        // Let the DOM update first, then sync classes for animations
        requestAnimationFrame(sync);

        // When the drawer closes, make sure scroll unlock happens
        if (t.matches('details.mobile-facets__disclosure') && !t.open) {
          FacetFiltersForm.unlockScroll(false);
          setTimeout(() => FacetFiltersForm.unlockScroll(false), 250);
        }
      },
      true
    );
  }

  // --- Close controls ---
  const bindOnce = (el, key, fn) => {
    if (!el) return;
    if (el.dataset[key] === '1') return;
    el.dataset[key] = '1';
    fn(el);
  };

  // Top-right X (full close)
  outerDetails.querySelectorAll('[data-drawer-close]').forEach((el) => {
    bindOnce(el, 'cbhFacetCloseBound', (node) => {
      // Keyboard for span role="button"
      if (node.getAttribute('role') === 'button') {
        node.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          node.click();
        });
      }

      node.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation?.();

          FacetFiltersForm.closeMobileFacetsDrawer(node);
        },
        true
      );
    });
  });

  // Back buttons (submenu only)
  outerDetails.querySelectorAll('.mobile-facets__close-button').forEach((el) => {
    bindOnce(el, 'cbhFacetBackBound', (node) => {
      if (node.tagName === 'BUTTON' && !node.getAttribute('type')) node.setAttribute('type', 'button');

      node.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation?.();

          const detailsEl = node.closest('details.mobile-facets__details');
          if (detailsEl) {
            detailsEl.removeAttribute('open');
            detailsEl.classList.remove('menu-opening');
          }

          requestAnimationFrame(sync);
        },
        true
      );
    });
  });

  // --- Live UI (kept from your version) ---
  if (mobileForm.dataset.cbhFacetLiveBound !== '1') {
    mobileForm.dataset.cbhFacetLiveBound = '1';

    const setApplyDirty = (dirty) => {
      mobileForm.dataset.cbhFacetDirty = dirty ? '1' : '0';

      mobileForm.querySelectorAll('button.cbh-apply').forEach((btn) => {
        const clean = btn.querySelector('[data-cbh-apply-clean]');
        const dirtyEl = btn.querySelector('[data-cbh-apply-dirty]');
        if (!clean || !dirtyEl) return;

        if (dirty) {
          clean.classList.add('hidden');
          dirtyEl.classList.remove('hidden');
        } else {
          dirtyEl.classList.add('hidden');
          clean.classList.remove('hidden');
        }
      });
    };

    const updateDetailsPill = (detailsEl) => {
      if (!detailsEl) return;

      const pill = detailsEl.querySelector(':scope > summary .cbh-mobile-facet-count');
      if (!pill) return;

      let count = 0;

      const anyCheckbox = detailsEl.querySelector('input[type="checkbox"]');
      if (anyCheckbox) {
        count = detailsEl.querySelectorAll('input[type="checkbox"]:checked').length;
      } else {
        const inputs = detailsEl.querySelectorAll('.facets__price input');
        count = Array.from(inputs).some((i) => String(i.value || '').trim() !== '') ? 1 : 0;
      }

      if (count > 0) {
        pill.textContent = String(count);
        pill.classList.remove('hidden');
      } else {
        pill.classList.add('hidden');
      }
    };

    const onAnyChange = (e) => {
      const t = e.target;
      if (!t) return;
      if (t.name === 'sort_by') return;

      const detailsEl = t.closest('details.mobile-facets__details');
      if (detailsEl) updateDetailsPill(detailsEl);

      setApplyDirty(true);
    };

    mobileForm.addEventListener(
      'click',
      (e) => {
        const btn = e.target.closest('button[data-cbh-close-facets]');
        if (!btn) return;
        setApplyDirty(false);
      },
      true
    );

    mobileForm.addEventListener('change', onAnyChange, true);
    mobileForm.addEventListener('input', onAnyChange, true);

    mobileForm.querySelectorAll('details.mobile-facets__details').forEach(updateDetailsPill);
  }
}

  /**
   * Mobile filter badge: shows number of active filters on the "Filter and sort" trigger.
   * Idempotent and safe to call after every render.
   */
  static updateMobileFilterBadge() {
    const open = document.querySelector('.mobile-facets__open');
    if (!open) return;

    let badge = open.querySelector('.cbh-facets-badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'cbh-facets-badge';
      badge.setAttribute('aria-hidden', 'true');
      open.appendChild(badge);
    }

    const active = document.querySelectorAll('.active-facets-mobile .active-facets__button').length;

    if (active > 0) {
      badge.textContent = String(active);
      badge.hidden = false;
      open.classList.add('cbh-has-active');
    } else {
      badge.hidden = true;
      open.classList.remove('cbh-has-active');
    }
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      productGridContainer.classList.add('loading');
      const collectionEl = productGridContainer.querySelector('.collection');
      if (collectionEl) collectionEl.classList.add('loading');
    }

    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        if (typeof initializeScrollAnimationTrigger === 'function') {
          try {
            initializeScrollAnimationTrigger();
          } catch (e) {}
        }

        // After swaps, make sure no scroll lock is stuck
        setTimeout(() => FacetFiltersForm.unlockScroll(false), 0);
        setTimeout(() => FacetFiltersForm.unlockScroll(false), 250);
      })
      .catch(() => {
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer) productGridContainer.classList.remove('loading');
        FacetFiltersForm.toggleActiveFacets(false);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const cached = FacetFiltersForm.filterData.find(filterDataUrl);
    if (!cached) return;

    const html = cached.html;

    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    if (typeof initializeScrollAnimationTrigger === 'function') {
      try {
        initializeScrollAnimationTrigger();
      } catch (e) {}
    }

    setTimeout(() => FacetFiltersForm.unlockScroll(false), 0);
    setTimeout(() => FacetFiltersForm.unlockScroll(false), 250);
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newContainer = parsed.getElementById('ProductGridContainer');
    if (!newContainer) return;

    productGridContainer.innerHTML = newContainer.innerHTML;

    productGridContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    productGridContainer.classList.remove('loading');
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    const source = parsed.getElementById('ProductCount');
    const sourceDesktop = parsed.getElementById('ProductCountDesktop');

    if (container && source) {
      container.innerHTML = source.innerHTML;
      container.classList.remove('loading');
    }

    if (containerDesktop) {
      if (sourceDesktop) {
        containerDesktop.innerHTML = sourceDesktop.innerHTML;
      } else if (source) {
        // Fallback if desktop count isn’t present in this template
        containerDesktop.innerHTML = source.innerHTML;
      }
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    // Update "Show X results" on the mobile Apply buttons (uses the freshest count text)
    const countText =
      (containerDesktop && containerDesktop.textContent) ||
      (container && container.textContent) ||
      '';

    const m = countText.replace(/\u00a0/g, ' ').match(/(\d[\d,]*)/); // first number
    if (m) {
      const n = parseInt(m[1].replace(/,/g, ''), 10);
      if (!Number.isNaN(n)) {
        document.querySelectorAll('[data-cbh-apply-count]').forEach((el) => {
          el.textContent = String(n);
        });
      }
    }

    // Update mobile badge
    FacetFiltersForm.updateMobileFilterBadge();
  }

  /**
   * ✅ Option B — known-good renderFilters (properly closed)
   */
  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const filterNodesSelector = [
      '#FacetFiltersForm .js-filter',
      '#FacetFiltersFormMobile .js-filter',
      '#FacetFiltersPillsForm .js-filter',
      '[id^="FacetFiltersForm-"] .js-filter',
      '[id^="FacetFiltersFormMobile-"] .js-filter',
      '[id^="FacetFiltersPillsForm-"] .js-filter',
    ].join(', ');

    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(filterNodesSelector);
    const facetDetailsElementsFromDom = document.querySelectorAll(filterNodesSelector);

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);

      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            const prev = document.getElementById(previousElementId);
            if (prev) prev.after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parent = document.getElementById(elementToRender.parentElement.id);
          if (parent) {
            const firstFilter = parent.querySelector('.js-filter');
            if (firstFilter) firstFilter.before(elementToRender);
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    // Desktop focus restore only (mobile drawer should not autofocus after render)
    if (countsToRender && event && event.target) {
      const activeForm = event.target.closest('form');
      const isMobileDrawer = Boolean(activeForm && activeForm.classList.contains('mobile-facets'));

      if (!isMobileDrawer) {
        const closestFilter = event.target.closest('.js-filter');
        const closestJSFilterID = closestFilter ? closestFilter.id : null;

        if (closestJSFilterID) {
          FacetFiltersForm.renderCounts(countsToRender, closestFilter);

          const newFacetDetailsElement = document.getElementById(closestJSFilterID);
          const newElementToActivate = newFacetDetailsElement?.querySelector('.facets__summary');
          const isTextInput = event.target.getAttribute('type') === 'text';

          if (newElementToActivate && !isTextInput) newElementToActivate.focus();
        }
      }
    }

    // Ensure newly-rendered mobile facet summaries/buttons work
    FacetFiltersForm.bindMobileFacetsDrawerControls();

    // And keep badge correct
    FacetFiltersForm.updateMobileFilterBadge();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;

      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);

    // Badge depends on active facets HTML
    FacetFiltersForm.updateMobileFilterBadge();
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) targetSummary.outerHTML = sourceSummary.outerHTML;

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');
    if (sourceHeaderElement && targetHeaderElement) targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement.querySelectorAll('.facets__item.hidden').forEach((hiddenItem) => {
          hiddenItem.classList.replace('hidden', 'show-more-item');
        });
      }
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static closeMobileFacetsDrawer(fromEl) {
  const outerDetails =
    (fromEl && fromEl.closest && fromEl.closest('details.mobile-facets__disclosure')) ||
    document.querySelector('details.mobile-facets__disclosure[open]') ||
    document.querySelector('details.mobile-facets__disclosure');

  if (!outerDetails) return;

  const drawer = outerDetails.closest('menu-drawer') || (fromEl && fromEl.closest && fromEl.closest('menu-drawer'));

  // Prefer theme API if present (scroll lock + focus management)
  if (drawer && typeof drawer.closeMenuDrawer === 'function') {
    try {
      const summary = drawer.querySelector('summary');
      drawer.closeMenuDrawer(null, summary || false);
    } catch (e) {}
  }

  // Hard reset (guaranteed)
  outerDetails.querySelectorAll('details.mobile-facets__details[open]').forEach((d) => {
    d.removeAttribute('open');
    d.classList.remove('menu-opening');
  });

  const main = outerDetails.querySelector('.mobile-facets__main');
  if (main) main.classList.remove('submenu-open');

    FacetFiltersForm.setFacetsOpenState(false);

  outerDetails.removeAttribute('open');
  outerDetails.classList.remove('menu-opening');

  FacetFiltersForm.unlockScroll(true);
  setTimeout(() => FacetFiltersForm.unlockScroll(false), 0);
  setTimeout(() => FacetFiltersForm.unlockScroll(false), 250);
}


  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
  // Prefer ProductGridContainer (most reliable across Dawn/Refresh variants)
  const pgc =
    document.getElementById('ProductGridContainer') ||
    document.querySelector('[id^="ProductGridContainer"]') ||
    document.querySelector('[data-id][id*="ProductGridContainer"]');

  let sectionId = pgc?.dataset?.id || pgc?.getAttribute?.('data-id') || null;

  // Fallback: product-grid
  if (!sectionId) {
    const grid =
      document.getElementById('product-grid') ||
      document.querySelector('[id^="product-grid"]') ||
      document.querySelector('[data-id][id*="product-grid"]');

    sectionId = grid?.dataset?.id || grid?.getAttribute?.('data-id') || null;
  }

  if (!sectionId) return [];
  return [{ section: sectionId }];
}

  // -------------------------
  // Instance helpers
  // -------------------------
  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const target = event.target;
    const allForms = document.querySelectorAll('facet-filters-form form');
    const activeForm = target ? target.closest('form') : null;

    const isMobile = Boolean(activeForm && activeForm.classList.contains('mobile-facets'));

    // Mobile: only allow sort change to submit here
    if (isMobile) {
      if (target && target.name === 'sort_by') {
        const searchParams = this.createSearchParams(activeForm);
        this.onSubmitForm(searchParams, event);
      }
      return;
    }

    // Desktop: build combined params (filters + sort)
    const forms = [];

    allForms.forEach((form) => {
      const id = form.id || '';

      const isMobileForm =
        form.classList.contains('mobile-facets') ||
        id === 'FacetFiltersFormMobile' ||
        id.startsWith('FacetFiltersFormMobile-');

      const isDesktopFiltersForm = id === 'FacetFiltersForm' || id.startsWith('FacetFiltersForm-');
      const isSortForm =
        id === 'FacetSortForm' ||
        id.startsWith('FacetSortForm') ||
        id === 'FacetSortDrawerForm' ||
        id.startsWith('FacetSortDrawerForm-');

      if (isMobileForm) return;
      if (isDesktopFiltersForm || isSortForm) forms.push(this.createSearchParams(form));
    });

    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf('?') === -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
  FacetFiltersForm.setListeners();
}

// -------------------------
// Price range
// -------------------------
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', minInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}

// -------------------------
// Facet remove
// -------------------------
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;

    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code && event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form && typeof form.onActiveFilterClick === 'function') form.onActiveFilterClick(event);
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove);
}

// ---------------------------------------------------------
// CBD HUT — Facets scroll-lock failsafe
// ---------------------------------------------------------
(() => {
  if (window.__CBH_FACETS_SCROLLSAFE__) return;
  window.__CBH_FACETS_SCROLLSAFE__ = true;

  const unlock = () => FacetFiltersForm.unlockScroll(false);
    // Initial sync (back/forward cache, page restore etc.)
  FacetFiltersForm.setFacetsOpenState(
    !!document.querySelector('details.mobile-facets__disclosure[open]')
  );

  // If the outer disclosure closes, unlock scroll
  document.addEventListener(
    'toggle',
    (e) => {
      const t = e.target;
      if (!t || !t.matches) return;

     if (t.matches('details.mobile-facets__disclosure')) {
  // Mark “facets open” globally (for z-index + hiding sticky chips outside the drawer)
  FacetFiltersForm.setFacetsOpenState(t.open);

  if (!t.open) {
    unlock();
    setTimeout(unlock, 250);
  }
}
    },
    true
  );

  // Also unlock if nothing facets-related is open
  document.addEventListener(
    'click',
    () => {
      const anyOpen =
        document.querySelector('details.mobile-facets__disclosure[open]') ||
        document.querySelector('.mobile-facets__details[open]');

      if (!anyOpen) {
        unlock();
        setTimeout(unlock, 250);
      }
    },
    true
  );
})();


// ---------------------------------------------------------
// CBH — Mobile facets: compute safe insets for floating widgets
// (TrustedSite / chat bubbles overlapping the Apply/Show results bar)
// ---------------------------------------------------------
(() => {
  if (window.__CBH_FACETS_SAFE_INSETS__) return;
  window.__CBH_FACETS_SAFE_INSETS__ = true;

  const WIDGET_SELECTORS = [
    '#trustedsite-badge',
    '#trustedsite',
    '.trustedsite',
    'iframe[src*="trustedsite" i]',
    'img[alt*="Trusted" i]',
    '#chat-widget',
    '.chat-widget',
    '.cc-window', // some cookie tools
  ].join(',');

  const getFooters = () =>
    Array.from(document.querySelectorAll('.mobile-facets__footer')).filter((el) => el.offsetParent !== null);

  const getWidgets = () =>
    Array.from(document.querySelectorAll(WIDGET_SELECTORS)).filter((el) => el.offsetParent !== null);

  function applySafeInsets() {
    const footers = getFooters();
    if (!footers.length) return;

    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;

    // Default: no inset
    let safeRight = 0;
    let safeBottom = 0;

    const widgets = getWidgets();
    if (widgets.length) {
      // Use the “worst” overlap against any visible footer
      footers.forEach((footer) => {
        const fr = footer.getBoundingClientRect();

        widgets.forEach((w) => {
          const r = w.getBoundingClientRect();

          // ignore tiny / offscreen
          if (r.width < 20 || r.height < 20) return;
          if (r.bottom < 0 || r.top > vh) return;

          const overlapsVertically = r.bottom > fr.top && r.top < fr.bottom;
          const isRightDocked = r.right > vw - 10;

          if (overlapsVertically && isRightDocked) {
            // how much space from the right edge the widget consumes
            safeRight = Math.max(safeRight, Math.max(0, vw - r.left) + 12);
          }

          const overlapsBottomBand = r.top < vh && r.bottom > vh - 140; // bottom UI band
          if (overlapsBottomBand) {
            safeBottom = Math.max(safeBottom, Math.max(0, vh - r.top) + 8);
          }
        });
      });
    }

    document.documentElement.style.setProperty('--cbh-facets-safe-right', `${safeRight}px`);
    document.documentElement.style.setProperty('--cbh-facets-safe-bottom', `${safeBottom}px`);
  }

  // Recalc when facets open/close + on resize (lightweight)
  const schedule = (() => {
    let raf = 0;
    return () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => applySafeInsets());
    };
  })();

  document.addEventListener(
    'toggle',
    (e) => {
      const t = e.target;
      if (!t || !t.matches) return;

      if (t.matches('details.mobile-facets__disclosure')) schedule();
      if (t.matches('.mobile-facets__details')) schedule();
    },
    true
  );

  window.addEventListener('resize', schedule, { passive: true });

  // Run once on load (in case widget is already present)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
})();

// ---------------------------------------------------------
// CBH — World-class facet search + UX upgrades (desktop + mobile)
// - UI-only filtering (no extra Shopify requests)
// - Pins active selections to top (even when NOT searching)
// - Remembers search input across AJAX re-render (sessionStorage)
// - Auto-scroll to first selected on open (mobile)
// - Keyboard: "/" focuses search, Enter toggles first visible, ESC clears
// - Desktop mini "Clear" in summary doesn't toggle <details>
// ---------------------------------------------------------
(() => {
  if (window.__CBH_FACET_SEARCH__) return;
  window.__CBH_FACET_SEARCH__ = true;

  const norm = (s) => String(s || '').trim().toLowerCase();

  const escapeHtml = (s) =>
    String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const STORAGE_PREFIX = 'cbhFacetSearch:';
  const storageKey = (facetKey) => `${STORAGE_PREFIX}${location.pathname}:${facetKey || ''}`;

  const isTypingTarget = (el) => {
    if (!el) return false;
    const tag = (el.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    return el.isContentEditable === true;
  };

  function ensureOrder(scope) {
    const items = scope.querySelectorAll('[data-cbh-facet-value]');
    items.forEach((li, idx) => {
      if (!li.hasAttribute('data-cbh-order')) li.setAttribute('data-cbh-order', String(idx));
    });
  }

  function highlight(li, q) {
    const labelEl = li.querySelector('.facet-checkbox__text-label');
    if (!labelEl) return;

    const original = labelEl.getAttribute('data-cbh-original');
    if (!original) labelEl.setAttribute('data-cbh-original', labelEl.textContent || '');

    const base = labelEl.getAttribute('data-cbh-original') || '';
    if (!q) {
      labelEl.textContent = base;
      return;
    }

    const hay = norm(base);
    const i = hay.indexOf(q);
    if (i < 0) {
      labelEl.textContent = base;
      return;
    }

    const before = escapeHtml(base.slice(0, i));
    const mid = escapeHtml(base.slice(i, i + q.length));
    const after = escapeHtml(base.slice(i + q.length));

    labelEl.innerHTML = `${before}<mark class="cbh-facet-mark">${mid}</mark>${after}`;
  }

  function reorderActiveFirst(scope, restrictToVisibleSet = null) {
    // Reorder within each parent list to preserve valid DOM structure
    const parents = new Map();
    scope.querySelectorAll('[data-cbh-facet-value]').forEach((li) => {
      const p = li.parentElement;
      if (!p) return;
      if (!parents.has(p)) parents.set(p, []);
      parents.get(p).push(li);
    });

    parents.forEach((arr, parent) => {
      const sorted = arr.slice().sort((a, b) => {
        const aActive = a.getAttribute('data-cbh-active') === 'true';
        const bActive = b.getAttribute('data-cbh-active') === 'true';

        // If restricted to visible set, keep visible before hidden
        if (restrictToVisibleSet) {
          const aV = restrictToVisibleSet.has(a);
          const bV = restrictToVisibleSet.has(b);
          if (aV !== bV) return aV ? -1 : 1;
        }

        // Active first
        if (aActive !== bActive) return aActive ? -1 : 1;

        // Stable original order
        return (
          (parseInt(a.getAttribute('data-cbh-order') || '0', 10) || 0) -
          (parseInt(b.getAttribute('data-cbh-order') || '0', 10) || 0)
        );
      });

      sorted.forEach((li) => parent.appendChild(li));
    });
  }

  function saveQuery(input) {
    const k = input.getAttribute('data-cbh-facet-key');
    if (!k) return;
    try {
      sessionStorage.setItem(storageKey(k), input.value || '');
    } catch (e) {}
  }

  function restoreQuery(input) {
    if (input.hasAttribute('data-cbh-restored')) return;
    const k = input.getAttribute('data-cbh-facet-key');
    if (!k) return;

    try {
      const v = sessionStorage.getItem(storageKey(k));
      if (v != null && v !== '') input.value = v;
    } catch (e) {}

    input.setAttribute('data-cbh-restored', 'true');
  }

  function autoScrollFirstSelected(detailsEl) {
    // Mobile only: scroll submenu to first checked option
    if (!detailsEl || !detailsEl.classList.contains('mobile-facets__details')) return;

    const submenu = detailsEl.querySelector('.mobile-facets__submenu');
    if (!submenu) return;

    const firstChecked = detailsEl.querySelector('input[type="checkbox"]:checked');
    if (!firstChecked) return;

    const li = firstChecked.closest('li');
    if (!li) return;

    // Try to keep it comfortably in view
    requestAnimationFrame(() => {
      const top = Math.max(0, li.offsetTop - 80);
      try {
        submenu.scrollTo({ top, behavior: 'smooth' });
      } catch (e) {
        submenu.scrollTop = top;
      }
    });
  }

  function apply(scope, input) {
    if (!scope || !input) return;

    ensureOrder(scope);
    restoreQuery(input);

    const q = norm(input.value);
    const isSearching = q.length > 0;
    scope.classList.toggle('cbh-is-searching', isSearching);

    const clearBtn = scope.querySelector('[data-cbh-facet-clear]');
    const countEl = scope.querySelector('[data-cbh-facet-count]');
    const emptyEl = scope.querySelector('[data-cbh-facet-empty]');

    if (clearBtn) clearBtn.classList.toggle('hidden', !isSearching);

    const items = Array.from(scope.querySelectorAll('[data-cbh-facet-value]'));
    const visible = new Set();

    let shown = 0;
    let matched = 0;
    let hasAnyActive = false;

    items.forEach((li) => {
      const label = norm(li.getAttribute('data-cbh-facet-value'));
      const active = li.getAttribute('data-cbh-active') === 'true';
      if (active) hasAnyActive = true;

      const labelMatches = !isSearching ? true : label.includes(q);

      // Premium: keep active visible even if not matching
      const isVisible = !isSearching ? true : (labelMatches || active);

      li.classList.toggle('cbh-facet-filtered-out', !isVisible);

      if (isVisible) {
        visible.add(li);
        shown += 1;
        if (labelMatches && isSearching) matched += 1;
      }

      // Highlight only when searching + label matches
      highlight(li, isSearching && labelMatches ? q : '');
    });

    // Reorder:
    // - while searching: active-first + visible-first
    // - while not searching: active-first (if any active values exist)
    if (isSearching) {
      reorderActiveFirst(scope, visible);
    } else if (hasAnyActive) {
      reorderActiveFirst(scope, null);
    }

    // Meta: count + empty state
    if (countEl) {
      if (isSearching) {
        countEl.textContent = matched === 1 ? '1 match' : `${matched} matches`;
        countEl.classList.remove('hidden');
      } else {
        countEl.classList.add('hidden');
      }
    }

    if (emptyEl) emptyEl.classList.toggle('hidden', !(isSearching && shown === 0));

    saveQuery(input);
  }

  function firstVisibleFacetInput() {
    // Priority: open mobile submenu, then open mobile drawer, then open desktop filter
    const openMobileSub = document.querySelector('.mobile-facets__details[open] [data-cbh-facet-input]');
    if (openMobileSub && openMobileSub.offsetParent) return openMobileSub;

    const openMobileDrawer = document.querySelector('details.mobile-facets__disclosure[open] [data-cbh-facet-input]');
    if (openMobileDrawer && openMobileDrawer.offsetParent) return openMobileDrawer;

    const openDesktop = document.querySelector('.facets .js-filter[open] [data-cbh-facet-input]');
    if (openDesktop && openDesktop.offsetParent) return openDesktop;

    // fallback: any visible
    const any = Array.from(document.querySelectorAll('[data-cbh-facet-input]')).find((el) => el.offsetParent);
    return any || null;
  }

  // Typing
  document.addEventListener(
    'input',
    (e) => {
      const t = e.target;
      if (!t || !t.matches || !t.matches('[data-cbh-facet-input]')) return;
      const scope = t.closest('[data-cbh-facet-scope]');
      if (!scope) return;
      apply(scope, t);
    },
    true
  );

  // Clear button
  document.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest('[data-cbh-facet-clear]');
      if (!btn) return;
      const scope = btn.closest('[data-cbh-facet-scope]');
      if (!scope) return;
      const input = scope.querySelector('[data-cbh-facet-input]');
      if (!input) return;

      input.value = '';
      apply(scope, input);
      input.focus();
    },
    true
  );

  // ESC clears (inside input)
  document.addEventListener(
    'keydown',
    (e) => {
      const t = e.target;
      if (!t || !t.matches || !t.matches('[data-cbh-facet-input]')) return;

      if (e.key === 'Escape') {
        const scope = t.closest('[data-cbh-facet-scope]');
        if (!scope) return;
        t.value = '';
        apply(scope, t);
        e.preventDefault();
      }

      // Enter toggles first visible item (don’t submit)
      if (e.key === 'Enter') {
        const scope = t.closest('[data-cbh-facet-scope]');
        if (!scope) return;

        const first = Array.from(scope.querySelectorAll('[data-cbh-facet-value]')).find((li) => {
          if (li.classList.contains('cbh-facet-filtered-out')) return false;
          if (!li.offsetParent) return false;
          const inputEl = li.querySelector('input[type="checkbox"]');
          if (!inputEl || inputEl.disabled) return false;
          return true;
        });

        if (first) {
          const box = first.querySelector('input[type="checkbox"]');
          if (box) box.click();
          e.preventDefault();
        }
      }
    },
    true
  );

  // "/" focuses facet search (global keyboard shortcut)
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== '/') return;
      if (isTypingTarget(e.target)) return;

      const input = firstVisibleFacetInput();
      if (!input) return;

      input.focus();
      e.preventDefault();
    },
    true
  );

  // Keep active state in sync + re-apply behaviour
  document.addEventListener(
    'change',
    (e) => {
      const t = e.target;
      if (!t || t.type !== 'checkbox') return;

      const li = t.closest('[data-cbh-facet-value]');
      if (li) li.setAttribute('data-cbh-active', t.checked ? 'true' : 'false');

      const scope = t.closest('[data-cbh-facet-scope]');
      if (!scope) return;

      const input = scope.querySelector('[data-cbh-facet-input]');
      if (input) apply(scope, input);
    },
    true
  );

  // When a filter panel opens: restore + apply + (mobile) auto-scroll to first selected
  document.addEventListener(
    'toggle',
    (e) => {
      const d = e.target;
      if (!d || !d.matches || !d.open) return;

      // Mobile auto-scroll
      if (d.classList.contains('mobile-facets__details')) autoScrollFirstSelected(d);

      const scope = d.querySelector('[data-cbh-facet-scope]');
      if (!scope) return;

      const input = scope.querySelector('[data-cbh-facet-input]');
      if (input) apply(scope, input);
    },
    true
  );

  // Desktop mini Clear link: don’t toggle details
  document.addEventListener(
    'pointerdown',
    (e) => {
      const a = e.target.closest('[data-cbh-facet-clear-mini]');
      if (!a) return;
      e.stopPropagation();
    },
    true
  );

  document.addEventListener(
    'click',
    (e) => {
      const a = e.target.closest('[data-cbh-facet-clear-mini]');
      if (!a) return;
      e.preventDefault();
      e.stopPropagation();
      const href = a.getAttribute('href');
      if (href) window.location.href = href;
    },
    true
  );

  // Restore saved searches after Shopify AJAX re-renders the facets DOM
  const restoreAll = () => {
    document.querySelectorAll('[data-cbh-facet-input][data-cbh-facet-key]').forEach((input) => {
      restoreQuery(input);
      const scope = input.closest('[data-cbh-facet-scope]');
      if (scope) apply(scope, input);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreAll, { once: true });
  } else {
    restoreAll();
  }

  // MutationObserver: re-apply on newly injected facet markup
  const obs = new MutationObserver((mutations) => {
    let found = false;
    for (const m of mutations) {
      for (const n of m.addedNodes) {
        if (!(n instanceof Element)) continue;
        if (n.querySelector && n.querySelector('[data-cbh-facet-input][data-cbh-facet-key]')) {
          found = true;
          break;
        }
      }
      if (found) break;
    }
    if (found) restoreAll();
  });

  obs.observe(document.documentElement, { childList: true, subtree: true });
})();

